/* eslint-disable no-template-curly-in-string */

// Simple library built on top of bmpn-moddle to create BPMN workflow files
// Warning, bpmn-moddle is based on callbacks, not promises...
// https://github.com/bpmn-io/moddle

import { camelCase, each, isFunction, findIndex, indexOf, isObject } from 'lodash-es'
import generateRandomId from 'vue-kerlink/dist/utils/generateRandomId'
import BpmnModdle from 'bpmn-moddle'
import ActivityMetaModel from '@/modules/bpmn/activiti-meta-model.json'

const moddle = new BpmnModdle([ActivityMetaModel])

class BpmnW {
  /**
  * @param workflowProperties {name, description, webservices[<ws metadata>]}
  * @param successCallback Function call when workflow is initialized
  */
  constructor (workflowProperties, successCallback) {
    this.properties = workflowProperties

    this._createBpmnModdle(successCallback)
  }

  /** PRIVATE **/

  /**
  * Create BPMN xml
  * @param successCallback Function call when workflow is initialized
  */
  _createBpmnModdle (successCallback) {
    // Base XML
    const xmlStr =
      `<?xml version="1.0" encoding="UTF-8"?>
      <definitions
        xmlns="http://www.omg.org/spec/BPMN/20100524/MODEL"
        xmlns:xsi="http://www.w3.org/2001/XMLSchema-instance"
        xmlns:xsd="http://www.w3.org/2001/XMLSchema"
        xmlns:activiti="http://activiti.org/bpmn"
        xmlns:bpmndi="http://www.omg.org/spec/BPMN/20100524/DI"
        xmlns:omgdc="http://www.omg.org/spec/DD/20100524/DC"
        xmlns:omgdi="http://www.omg.org/spec/DD/20100524/DI"
        typeLanguage="http://www.w3.org/2001/XMLSchema"
        expressionLanguage="http://www.w3.org/1999/XPath"
        targetNamespace="http://www.activiti.org/processdef">
      </definitions>`

    moddle.fromXML(xmlStr, (err, definitions) => {
      if (err) {
        console.error(err)
        return false
      }

      // Save root
      this.definitions = definitions

      // Add root process
      this.rootProcess = moddle.create('bpmn:Process', {
        id: camelCase(this.properties.name),
        name: this.properties.name,
        isExecutable: true
      })
      this.definitions.get('rootElements').push(this.rootProcess)

      // Add stringified json into description to use it instead of parsing all XML
      this.rootProcess.get('flowElements').push(moddle.create('bpmn:Documentation', { text: this.properties.description }))

      // Add start event
      this.startEvent = moddle.create('bpmn:StartEvent', { id: 'startEvent' })
      this.rootProcess.get('flowElements').push(this.startEvent)

      // Add end event
      this.endEvent = moddle.create('bpmn:EndEvent', { id: 'endEvent' })
      this.rootProcess.get('flowElements').push(this.endEvent)

      // Loop throught selected webservices to add tasks
      const tasks = []
      each(this.properties.webservices, webservice => {
        tasks.push(this._addTask(webservice))
      })

      // Add link between start event and first webservice
      this._addLink('start', tasks[0])

      // Loop throught tasks to add links between webservices (the last one will be linked to end event)
      for (let i = 0; i < tasks.length - 1; i++) {
        const task = tasks[i]
        this._addLink(task, tasks[i + 1])
      }

      // Add link between last webservice and end event
      this._addLink(tasks[tasks.length - 1], 'end')

      // Call successCallback
      if (isFunction(successCallback)) {
        successCallback()
      }
    })
  }

  /**
  * Add task
  * @param webserviceProperties {}
  * @return {startHook, endHook} An object with start and end hook to link them later
  */
  _addTask (webserviceProperties) {
    // Create activiti extension element for serviceTask
    const serviceActiviti = moddle.create('bpmn:ExtensionElements')

    // webService name
    const webserviceName = moddle.create('activiti:FormProperty', {
      id: 'name',
      name: 'name',
      type: 'string',
      variable: webserviceProperties.name,
      writable: false,
      required: true
    })
    serviceActiviti.get('values').push(webserviceName)

    // webService url
    const webservice = moddle.create('activiti:FormProperty', {
      id: 'webService',
      name: 'webService',
      type: 'string',
      variable: webserviceProperties.path,
      writable: false,
      required: true
    })
    serviceActiviti.get('values').push(webservice)

    // webService method
    const httpMethod = moddle.create('activiti:FormProperty', {
      id: 'httpMethod',
      name: 'httpMethod',
      type: 'string',
      variable: webserviceProperties.method.toUpperCase(),
      writable: false,
      required: true
    })
    serviceActiviti.get('values').push(httpMethod)

    // webService type
    const wsType = moddle.create('activiti:FormProperty', {
      id: 'wsType',
      name: 'wsType',
      type: 'string',
      variable: webserviceProperties.asynchronous ? 'asynchronous' : 'synchronous',
      writable: false,
      required: true
    })
    serviceActiviti.get('values').push(wsType)

    // needs a dto or not? if yes, argType='dto'
    const argType = moddle.create('activiti:FormProperty', {
      id: 'argType',
      name: 'argType',
      type: 'string',
      variable: findIndex(webserviceProperties.parameters, param => param.direction === 'REQUEST' && param.location === 'BODY' && param.dto) !== -1 ? 'dto' : null,
      writable: false,
      required: true
    })
    serviceActiviti.get('values').push(argType)

    // webservice parameters
    each(webserviceProperties.parameters, parameter => {
      // Do not insert header parameters
      if (parameter.location === 'HEADER') return true

      // Some parameters are systematically send or not relevant, do not insert them in bpmn
      const notTheseParams = ['customerId', 'fleetId', 'clusterId', 'eui', 'euis', 'devEui', 'endDeviceId', 'fields', 'criteria']
      if (indexOf(notTheseParams, parameter.name) !== -1) return true

      if (parameter.direction === 'REQUEST' && parameter.location === 'BODY' && parameter.dto) {
        // If parameter is a DTO send in BODY, loop through dto
        // and take only authorized parameters
        each(parameter.dto.fields, field => {
          if (!field.authorized) return true

          // Set parameter metadata
          if (field.set) field.type = 'enum'
          const paramMeta = moddle.create('activiti:FormProperty', {
            id: field.name,
            name: field.name,
            type: field.type,
            variable: field.set || '',
            writable: false,
            required: field.mandatory
          })
          serviceActiviti.get('values').push(paramMeta)
        })
      } else {
        // Non dto parameter
        // Set parameter metadata
        if (parameter.set) parameter.type = 'enum'
        const paramMeta = moddle.create('activiti:FormProperty', {
          id: parameter.name,
          name: parameter.name,
          type: parameter.type,
          variable: parameter.set,
          writable: false,
          required: parameter.mandatory
        })
        serviceActiviti.get('values').push(paramMeta)
      }
    })

    // Add user task with parameters
    const webserviceUserTask = moddle.create('bpmn:UserTask', {
      id: this._generateId(),
      name: webserviceProperties.name,
      'activiti:assignee': 'workflowStepExecutor',
      extensionElements: serviceActiviti
    })
    this.rootProcess.get('flowElements').push(webserviceUserTask)

    // Create activiti extension element for userTask
    const userActiviti = moddle.create('bpmn:ExtensionElements')

    // webService url
    const webserviceResult = moddle.create('activiti:FormProperty', { name: `check result of ${webserviceProperties.name}` })
    const webserviceSuccess = moddle.create('activiti:Value', { name: 'true' })
    const webserviceFailure = moddle.create('activiti:Value', { name: 'false' })
    webserviceResult.get('values').push(webserviceSuccess)
    webserviceResult.get('values').push(webserviceFailure)
    userActiviti.get('values').push(webserviceResult)

    // Add user task
    const userTask = moddle.create('bpmn:UserTask', {
      id: this._generateId(),
      name: webserviceProperties.name,
      'activiti:assignee': 'tasksStatusCheck',
      extensionElements: userActiviti
    })
    this.rootProcess.get('flowElements').push(userTask)

    // Link service task to user task
    const serviceToUserLink = moddle.create('bpmn:SequenceFlow', {
      id: this._generateId(),
      sourceRef: webserviceUserTask,
      targetRef: userTask
    })
    this.rootProcess.get('flowElements').push(serviceToUserLink)

    return {
      sourceRef: webserviceUserTask,
      targetRef: userTask
    }
  }

  /**
  * Add link between two nodes
  * @param startHook Bpmn group node from addTask {startHook, endHook} or string ['start'|'end']
  * @param endHook Bpmn group node from addTask {startHook, endHook} or string ['start'|'end']
  */
  _addLink (startHook, endHook) {
    let sourceRef = startHook
    let targetRef = endHook

    // Check if we need to add an exclusive gateway (if not link to start or end)
    const addExclusiveGateway = startHook !== 'start' && endHook !== 'end'

    // Start values
    if (startHook === 'start') {
      sourceRef = this.startEvent
    } else if (isObject(startHook) && startHook.targetRef) {
      sourceRef = startHook.targetRef
    }

    // End values
    if (endHook === 'end') {
      targetRef = this.endEvent
    } else if (isObject(endHook) && endHook.sourceRef) {
      targetRef = endHook.sourceRef
    }

    if (addExclusiveGateway) {
      // Add exclusive gateway if needed
      const exclusiveGateway = moddle.create('bpmn:ExclusiveGateway', { id: this._generateId() })
      this.rootProcess.get('flowElements').push(exclusiveGateway)

      // Link sourceRef to this exclusive gateway
      const linkSource = moddle.create('bpmn:SequenceFlow', {
        id: this._generateId(),
        sourceRef: sourceRef,
        targetRef: exclusiveGateway
      })
      this.rootProcess.get('flowElements').push(linkSource)

      // Link success to targetRef
      const linkSuccess = moddle.create('bpmn:SequenceFlow', {
        id: this._generateId(),
        sourceRef: exclusiveGateway,
        targetRef: targetRef,
        conditionExpression: moddle.create('bpmn:FormalExpression', { body: '${ result == "true" }' })
      })
      this.rootProcess.get('flowElements').push(linkSuccess)

      // Link failure to end event
      const linkFailure = moddle.create('bpmn:SequenceFlow', {
        id: this._generateId(),
        sourceRef: exclusiveGateway,
        targetRef: this.endEvent,
        conditionExpression: moddle.create('bpmn:FormalExpression', { body: '${ result == "false" }' })
      })
      this.rootProcess.get('flowElements').push(linkFailure)
    } else {
      // Link between source and target without exclusive gateway
      const link = moddle.create('bpmn:SequenceFlow', {
        id: this._generateId(),
        sourceRef: sourceRef,
        targetRef: targetRef
      })
      this.rootProcess.get('flowElements').push(link)
    }
  }

  // Generate a random id sid-xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx
  _generateId () {
    const id =
      generateRandomId(4) + generateRandomId(4) + '-' +
      generateRandomId(4) + '-' +
      generateRandomId(4) + '-' +
      generateRandomId(4) + '-' +
      generateRandomId(4) + generateRandomId(4) + generateRandomId(4)

    return 'sid-' + id.toUpperCase()
  }

  /** PUBLIC **/

  /**
  * Export to XML
  * @param workflowProperties {name, description}
  * @param successCallback Function call when workflow is initialized
  */
  exportXml (successCallback) {
    moddle.toXML(this.definitions, (err, updatedDefinition) => {
      if (err) {
        console.error(err)
        return false
      }

      // Call successCallback
      if (isFunction(successCallback)) {
        successCallback(updatedDefinition)
      }
    })
  }
}

export default BpmnW
